import React, { useState, useRef } from 'react';
import { X, Download, Upload, Trash2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { contactService } from '../services/contactService';
import '../styles/ImportContactsModal.css';

interface ImportContactsModalProps {
  workspaceId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface ContactRow {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  prefix: string;
  suffix: string;
  displayName: string;
  contactType: string;
  email: string;
  emailType: string;
  phone: string;
  phoneType: string;
  country: string;
  state: string;
  doNotCall: boolean;
  doNotEmail: boolean;
  doNotSMS: boolean;
  allowLogin: boolean;
  errors: string[];
}

const ImportContactsModal: React.FC<ImportContactsModalProps> = ({
  workspaceId,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const templateHeaders = [
    'First Name*',
    'Middle Name',
    'Last Name*',
    'Prefix',
    'Suffix',
    'Display Name',
    'Contact Type*',
    'Email*',
    'Email Type',
    'Phone',
    'Phone Type',
    'Country',
    'State',
    'Do Not Call',
    'Do Not Email',
    'Do Not SMS',
    'Allow Login'
  ];

  const downloadTemplate = (format: 'csv' | 'xlsx') => {
    const templateData = [
      templateHeaders,
      [
        'John',
        'Michael',
        'Doe',
        'Mr.',
        'Jr.',
        'John M. Doe Jr.',
        'End-User',
        'john.doe@example.com',
        'Work',
        '1234567890',
        'Mobile',
        'USA',
        'California',
        'FALSE',
        'FALSE',
        'FALSE',
        'FALSE'
      ]
    ];

    if (format === 'csv') {
      const csvContent = templateData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'contact_import_template.csv';
      link.click();
      toast.success('CSV template downloaded');
    } else {
      const ws = XLSX.utils.aoa_to_sheet(templateData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contacts');
      XLSX.writeFile(wb, 'contact_import_template.xlsx');
      toast.success('Excel template downloaded');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = event.target?.result;
        let parsedData: any[][] = [];

        if (file.name.endsWith('.csv')) {
          // Parse CSV
          const text = data as string;
          parsedData = text.split('\n').map(row =>
            row.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
          );
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // Parse Excel
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        } else {
          toast.error('Unsupported file format. Please upload CSV or XLSX file.');
          return;
        }

        // Skip header row and process data
        const headers = parsedData[0];
        const dataRows = parsedData.slice(1).filter(row => row.some(cell => cell));

        const processedContacts: ContactRow[] = dataRows.map((row, index) => {
          const contact: ContactRow = {
            id: `temp-${Date.now()}-${index}`,
            firstName: row[0] || '',
            middleName: row[1] || '',
            lastName: row[2] || '',
            prefix: row[3] || '',
            suffix: row[4] || '',
            displayName: row[5] || '',
            contactType: row[6] || '',
            email: row[7] || '',
            emailType: row[8] || 'Work',
            phone: row[9] || '',
            phoneType: row[10] || 'Mobile',
            country: row[11] || '',
            state: row[12] || '',
            doNotCall: row[13] === 'TRUE' || row[13] === 'true',
            doNotEmail: row[14] === 'TRUE' || row[14] === 'true',
            doNotSMS: row[15] === 'TRUE' || row[15] === 'true',
            allowLogin: row[16] === 'TRUE' || row[16] === 'true',
            errors: []
          };

          // Validate required fields
          if (!contact.firstName.trim()) contact.errors.push('First Name is required');
          if (!contact.lastName.trim()) contact.errors.push('Last Name is required');
          if (!contact.contactType.trim()) contact.errors.push('Contact Type is required');
          if (!contact.email.trim()) contact.errors.push('Email is required');

          // Validate email format
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (contact.email && !emailRegex.test(contact.email)) {
            contact.errors.push('Invalid email format');
          }

          return contact;
        });

        setContacts(processedContacts);
        setStep('preview');
        toast.success(`${processedContacts.length} contacts loaded`);
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Failed to parse file. Please check the format.');
      }
    };

    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  const handleFieldChange = (id: string, field: keyof ContactRow, value: any) => {
    setContacts(prev => prev.map(contact => {
      if (contact.id === id) {
        const updated = { ...contact, [field]: value };

        // Re-validate
        updated.errors = [];
        if (!updated.firstName.trim()) updated.errors.push('First Name is required');
        if (!updated.lastName.trim()) updated.errors.push('Last Name is required');
        if (!updated.contactType.trim()) updated.errors.push('Contact Type is required');
        if (!updated.email.trim()) updated.errors.push('Email is required');

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (updated.email && !emailRegex.test(updated.email)) {
          updated.errors.push('Invalid email format');
        }

        return updated;
      }
      return contact;
    }));
  };

  const handleRemoveContact = (id: string) => {
    setContacts(prev => prev.filter(c => c.id !== id));
  };

  const handleImport = async () => {
    const validContacts = contacts.filter(c => c.errors.length === 0);

    if (validContacts.length === 0) {
      toast.error('No valid contacts to import. Please fix errors first.');
      return;
    }

    if (validContacts.length < contacts.length) {
      const proceed = window.confirm(
        `${contacts.length - validContacts.length} contact(s) have errors and will be skipped. Continue with ${validContacts.length} valid contact(s)?`
      );
      if (!proceed) return;
    }

    try {
      setImporting(true);

      // Transform contacts to API format
      const contactsData = validContacts.map(contact => ({
        firstName: contact.firstName,
        middleName: contact.middleName || undefined,
        lastName: contact.lastName,
        prefix: contact.prefix || undefined,
        suffix: contact.suffix || undefined,
        displayName: contact.displayName || undefined,
        contactType: contact.contactType.split(',').map(t => t.trim()),
        emails: [{
          type: contact.emailType,
          email: contact.email,
          isPrimary: true,
          htmlSupported: true,
          marketing: false,
          isInactive: false
        }],
        phones: contact.phone ? [{
          type: contact.phoneType,
          number: contact.phone,
          isPrimary: true,
          smsOptIn: !contact.doNotSMS,
          isInactive: false
        }] : [],
        geography: (contact.country || contact.state) ? {
          country: contact.country,
          state: contact.state
        } : undefined,
        doNotCall: contact.doNotCall,
        doNotEmail: contact.doNotEmail,
        doNotSMS: contact.doNotSMS,
        allowLogin: contact.allowLogin
      }));

      // Import contacts in batch
      const result = await contactService.bulkImport(workspaceId, contactsData);
      toast.success(`Successfully imported ${result.imported || validContacts.length} contact(s)`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error importing contacts:', error);
      toast.error(error.message || 'Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="import-modal-overlay" onClick={onClose}>
      <div className="import-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="import-modal-header">
          <h2>Import Contacts</h2>
          <button className="import-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {step === 'upload' && (
          <div className="import-modal-body">
            <div className="import-instructions">
              <h3>Step 1: Download Template</h3>
              <p>Download the template file and fill in your contact information.</p>
              <div className="import-template-buttons">
                <button
                  className="btn btn-outline btn-md"
                  onClick={() => downloadTemplate('csv')}
                >
                  <Download size={18} />
                  Download CSV Template
                </button>
                <button
                  className="btn btn-outline btn-md"
                  onClick={() => downloadTemplate('xlsx')}
                >
                  <Download size={18} />
                  Download Excel Template
                </button>
              </div>
            </div>

            <div className="import-instructions">
              <h3>Step 2: Upload File</h3>
              <p>Upload your completed CSV or Excel file with contact data.</p>
              <div className="import-upload-area">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={18} />
                  Choose File
                </button>
                {fileName && (
                  <p className="import-file-name">Selected: {fileName}</p>
                )}
              </div>
            </div>

            <div className="import-note">
              <AlertCircle size={18} />
              <div>
                <strong>Note:</strong> Fields marked with * are required. Contact Type should be one of: End-User, Broker, Agent, Investor, Partner, Vendor, Other.
              </div>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <>
            <div className="import-modal-body">
              <div className="import-preview-header">
                <h3>Preview & Edit ({contacts.length} contacts)</h3>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setStep('upload');
                    setContacts([]);
                    setFileName('');
                  }}
                >
                  Upload Different File
                </button>
              </div>

              <div className="import-preview-table-container">
                <table className="import-preview-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>First Name*</th>
                      <th>Last Name*</th>
                      <th>Email*</th>
                      <th>Contact Type*</th>
                      <th>Phone</th>
                      <th>Country</th>
                      <th>State</th>
                      <th style={{ width: '60px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr key={contact.id} className={contact.errors.length > 0 ? 'has-errors' : ''}>
                        <td>
                          {contact.errors.length > 0 && (
                            <span className="error-indicator" title={contact.errors.join(', ')}>
                              <AlertCircle size={16} />
                            </span>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={contact.firstName}
                            onChange={(e) => handleFieldChange(contact.id, 'firstName', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={contact.lastName}
                            onChange={(e) => handleFieldChange(contact.id, 'lastName', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <input
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleFieldChange(contact.id, 'email', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={contact.contactType}
                            onChange={(e) => handleFieldChange(contact.id, 'contactType', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={contact.phone}
                            onChange={(e) => handleFieldChange(contact.id, 'phone', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={contact.country}
                            onChange={(e) => handleFieldChange(contact.id, 'country', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={contact.state}
                            onChange={(e) => handleFieldChange(contact.id, 'state', e.target.value)}
                            className="import-table-input"
                          />
                        </td>
                        <td>
                          <button
                            className="import-remove-btn"
                            onClick={() => handleRemoveContact(contact.id)}
                            title="Remove contact"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {contacts.filter(c => c.errors.length > 0).length > 0 && (
                <div className="import-error-summary">
                  <AlertCircle size={18} />
                  <span>
                    {contacts.filter(c => c.errors.length > 0).length} contact(s) have errors. Please fix them or they will be skipped.
                  </span>
                </div>
              )}
            </div>

            <div className="import-modal-footer">
              <button
                className="btn btn-secondary btn-md"
                onClick={onClose}
                disabled={importing}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleImport}
                disabled={importing || contacts.length === 0}
              >
                {importing ? 'Importing...' : `Import ${contacts.filter(c => c.errors.length === 0).length} Contacts`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportContactsModal;
